import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  getOverseasContacts,
  OverseasContactsResponse,
} from '../../../endpoints/chaptersTs';
import { Search } from '@mui/icons-material';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import { getGroupRolesByType } from '../../../endpoints/groups';
import {
  INTL_GROUP_TYPE_OVERSEAS,
  IntlSaveData,
  saveIntlLeadershipPosition,
} from './intlLeadershipEdit';
import AlertItem from '../../../Components/AlertItem';

const IntlMembers: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);

  const { data: reciprocityData, isFetching } =
    useApiGet<OverseasContactsResponse>(
      ['intl-leadership'],
      getOverseasContacts,
      {
        enabled: true,
        retry: 1,
      },
      {}
    );

  const canEdit = Boolean(reciprocityData?.can_edit);
  const contacts = reciprocityData?.data || [];

  const { data: overseasRoles } = useApiGet(
    ['intl-overseas-roles'],
    () => getGroupRolesByType(INTL_GROUP_TYPE_OVERSEAS),
    { enabled: canEdit, retry: 1 },
    {}
  );

  const filteredReciprocities =
    search.length > 0
      ? contacts.filter((reciprocity) =>
          reciprocity.name.toLowerCase().includes(search.toLowerCase())
        )
      : contacts;

  const onSavePosition = (position: IntlSaveData) => {
    setIsSaving(true);
    setErrorSaving(null);
    void saveIntlLeadershipPosition(position)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['intl-leadership'] });
      })
      .catch((err: { message?: string }) => {
        setErrorSaving(err?.message || 'Failed to save position.');
      })
      .finally(() => setIsSaving(false));
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!reciprocityData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      {errorSaving && (
        <Grid item xs={12} sx={{ px: 2, pt: 2 }}>
          <AlertItem
            severity="error"
            description={errorSaving}
            onClose={() => setErrorSaving(null)}
          />
        </Grid>
      )}
      {contacts.length > 2 && (
        <Grid item xs={12} sx={{ padding: 2 }}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={search}
            fullWidth={true}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}
      {filteredReciprocities.map((reciprocity) => (
        <Grid item xs={12} md={6} sx={{ padding: 2 }} key={reciprocity.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title={reciprocity.name} sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {(reciprocity.members || [])
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((member) => (
                    <PositionDisplay
                      key={member.id}
                      id={member.id}
                      position={member.role || ''}
                      positionId={member.role_id || ''}
                      committeeId={
                        member.committee_id || member.group_id || reciprocity.id
                      }
                      name={member.name}
                      startDate={member.start_date}
                      endDate={member.end_date}
                      canEdit={canEdit}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={canEdit ? onSavePosition : null}
                      userId={member.user_id}
                      pageFrom={'intl-overseas'}
                      canMessage={true}
                    />
                  ))}
                {canEdit && (
                  <Grid item xs={12} sx={{ p: 1 }}>
                    <AddPosition
                      buttonText="Add Contact"
                      committeeId={reciprocity.id}
                      positions={(overseasRoles || []).map((r: any) => ({
                        id: r.id,
                        name: r.name,
                        order: r.order,
                      }))}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                      saveSuccess={null}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default IntlMembers;
